import { ZILLOW_MIN_INTERVAL } from '../constants/property-types';
import { supabase } from '../supabase';

let lastZillowRequestTime = 0;

// Rate limit event emitter
export const rateLimitEvents = {
  listeners: new Set<(message: string) => void>(),
  emit(message: string) {
    this.listeners.forEach(listener => listener(message));
  },
  subscribe(listener: (message: string) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
};

export async function rateLimitedFetch(url: string, options: any = {}) {
  const rateLimitStartTime = Date.now();
  
  // Only apply rate limiting to Zillow API calls, not database operations
  if (url.includes('zillow-com4.p.rapidapi.com')) {
    const now = Date.now();
    const wait = Math.max(0, lastZillowRequestTime + 1000 - now); // 1 second between requests for 2 req/sec limit
    if (wait > 0) {
      await new Promise(res => setTimeout(res, wait));
    }
    lastZillowRequestTime = Date.now();
  }
  
  const response = await fetch(url, options);
  
  return response;
}

/**
 * Enhanced fetch function with better error handling for rate limits and API errors
 */
export async function enhancedFetch(url: string, options: any = {}) {
  const fetchStartTime = Date.now();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  
  try {
    // Resolve relative /api calls against a configured HTTPS base in native/webview contexts
    try {
      const isAbsolute = /^(https?:)?\/\//i.test(url);
      const isApiPath = typeof url === 'string' && url.startsWith('/api/');
      if (!isAbsolute && isApiPath) {
        const base = (import.meta as any)?.env?.VITE_API_BASE_URL as string | undefined;
        if (base) {
          const trimmedBase = base.replace(/\/$/, '');
          url = `${trimmedBase}${url}`;
        }
      }
    } catch {}

    // Apply a 10s client-side timeout via AbortController
    const controller = new AbortController();
    const timeoutMs = 10000;
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    // Attach Authorization header if missing and we have a session
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      const headers = new Headers(options.headers || {});
      if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      options = { ...options, headers };
    } catch {}

    const response = await rateLimitedFetch(url, { ...options, signal: controller.signal });
    const durationMs = Date.now() - fetchStartTime;
    if (!response.ok) {
      // Handle non-OK responses
    }
    
    if (!response.ok) {
      const errorData = await response.text();
      let parsedError;
      
      try {
        parsedError = JSON.parse(errorData);
      } catch {
        parsedError = { message: errorData };
      }
      
      // Handle error response
      
      // Handle specific error cases
      if (response.status === 429) {
        const retryAfter = parsedError.retryAfter || 60;
        const errorMessage = `Rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`;
        rateLimitEvents.emit(errorMessage);
        throw new Error(errorMessage);
      } else if (response.status === 401) {
        throw new Error('Authentication failed. Please check your credentials.');
      } else if (response.status === 403) {
        throw new Error('Access denied. You may not have permission to access this resource.');
      } else if (response.status >= 500) {
        throw new Error('Service temporarily unavailable. Please try again later.');
      } else {
        throw new Error(parsedError.message || `Request failed with status ${response.status}`);
      }
    }
    
    return response;
  } catch (error) {
    const fetchDuration = Date.now() - fetchStartTime;
    if ((error as any)?.name === 'AbortError') {
      throw new Error('Request timed out after 10 seconds');
    }
    
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while making the request.');
  }
  finally {
    // Always clear the timeout if it was set
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/**
 * Helper function to convert Zillow property type codes to readable strings
 */
export function getPropertyTypeFromCode(code: number): string {
  const PROPERTY_TYPE_MAP: Record<number, string> = {
    6: 'Single family home',
    3: 'Condo',
    13: 'Townhome',
    4: 'Multi family home',
    10: 'Mobile',
    8: 'Land',
    5: 'Apartment',
  };
  return PROPERTY_TYPE_MAP[code] || 'Unknown';
}

/**
 * Extracts the most "searchable" part of a user's input for Zillow API queries.
 * For a full address, returns just the street address (e.g., "4060 Lovers Ln").
 * For other input, returns the input as-is.
 */
export function extractSearchableTerm(input: string): string {
  if (!input) return '';
  // Lowercase and trim
  let term = input.trim();
  // Remove city, state, and zip if present
  // e.g. "4060 Lovers Ln, University Park, TX 75225" -> "4060 Lovers Ln"
  const addressMatch = term.match(/^([0-9]+ [^,]+)/);
  if (addressMatch) {
    return addressMatch[1];
  }
  // If not a full address, just return the input
  return input;
} 